import React, { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import apiService from "../services/api";
import { processGalleryImageUrl } from "../utils/imageUtils";
import RollingGallery from "./RollingGallery";
import Squares from "./Squares";

const FeaturedCarsBlock = () => {
  const [galleryImages, setGalleryImages] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [failedImages, setFailedImages] = useState(new Set());

  useEffect(() => {
    // Fallback images if API fails
    const fallbackImages = [
      '/assets/AUDI Q3 IMAGE.jpeg',
      '/assets/KWID IMAGE.jpeg',
      '/assets/SELTOS IMAGE.jpeg',
      '/assets/MERCEDES C220D IMAGE.jpeg',
      '/assets/HONDA CITY IMAGE.jpeg',
      '/assets/BALENO IMAGE.jpeg'
    ];

    const loadGalleryImages = async () => {
      try {
        // Fetch both gallery images and car images
        const [galleryResponse, carImagesResponse] = await Promise.all([
          apiService.getGalleryImages('featured'),
          apiService.getFeaturedCarImages(8) // Get images from up to 8 cars
        ]);

        let combinedImages = [];

        // Add gallery images first (if any) - filter out failed images
        if (galleryResponse.success && galleryResponse.data.length > 0) {
          const galleryImageUrls = galleryResponse.data
            .map(img => processGalleryImageUrl(img.imageUrl))
            .filter(url => !failedImages.has(url)); // Exclude previously failed images
          combinedImages = [...galleryImageUrls];
        }

        // Add car images from the uploads directory - filter out failed images
        if (carImagesResponse.success && carImagesResponse.data.length > 0) {
          const carImageUrls = carImagesResponse.data
            .map(img => img.imageUrl)
            .filter(url => !failedImages.has(url)); // Exclude previously failed images
          combinedImages = [...combinedImages, ...carImageUrls];
        }

        // Use combined images if we have any, otherwise fallback
        if (combinedImages.length > 0) {
          // Remove duplicates and limit to reasonable number for performance
          const uniqueImages = [...new Set(combinedImages)].slice(0, 15);
          setGalleryImages(uniqueImages);
        } else {
          // Use fallback images only if no images are available from either source
          setGalleryImages(fallbackImages);
        }
      } catch (error) {
        console.error('Failed to load gallery images:', error);
        setError('Failed to load gallery images');
        // Fallback to local images
        setGalleryImages(fallbackImages);
      } finally {
        setLoading(false);
      }
    };

    loadGalleryImages();
    
    // Refresh gallery images every 30 seconds to catch new uploads
    const refreshInterval = setInterval(loadGalleryImages, 30000);
    
    return () => clearInterval(refreshInterval);
  }, [failedImages]);

  // Add error handling for images
  const handleImageError = (e, imageUrl) => {
    console.warn(`Failed to load image: ${imageUrl}`);
    
    // Add to failed images set to prevent future retries
    setFailedImages(prev => new Set([...prev, imageUrl]));
    
    // Set placeholder and prevent infinite loop
    e.target.src = '/img/placeholder.svg';
    e.target.onerror = null;
    
    // Remove the failed image from current gallery to prevent continuous errors
    setGalleryImages(prev => prev.filter(img => img !== imageUrl));
  };

  return (
    <section className="full-viewport featured-cars-section">
      <div className="gallery-container-with-bg" style={{ position: "relative" }}>
        {/* Squares Background - Only behind the gallery */}
        <div className="squares-background-gallery">
          <Squares 
            speed={0.5} 
            squareSize={40}
            direction='diagonal'
            borderColor='rgba(255, 255, 255, 0.3)'
            hoverFillColor='rgba(255, 255, 255, 0.1)'
          />
        </div>
        
        {/* Gallery Content */}
        <div className="gallery-content-overlay">
          {loading ? (
            <div className="gallery-loading">
              <div className="spinner"></div>
              <p>Loading gallery...</p>
            </div>
          ) : (
            <RollingGallery 
              autoplay={true} 
              pauseOnHover={true} 
              images={galleryImages}
              onImageError={handleImageError}
            />
          )}
          {error && (
            <div className="gallery-error">
              <p>Using fallback images</p>
            </div>
          )}
        </div>
      </div>

      <div style={{ textAlign: "center", marginTop: "2rem" }}>
        <Link to="/cars" className="btn btn-secondary">View All Cars</Link>
      </div>
    </section>
  );
};

export default FeaturedCarsBlock;
